import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';

const AddStudentForm = ({ tenantId, schools = [], onSuccess, onCancel }) => {
  const [studentName, setStudentName] = useState('');
  const [selectedCaseManager, setSelectedCaseManager] = useState('');
  const [selectedEceRecorder, setSelectedEceRecorder] = useState(''); // <-- NEW STATE
  const [schoolId, setSchoolId] = useState('');
  
  const [availableManagers, setAvailableManagers] = useState([]);
  const [availableEceRecorders, setAvailableEceRecorders] = useState([]); // <-- NEW STATE
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Fetch the list of Case Managers AND ECE Recorders when the form opens
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const staffRef = collection(db, 'districts', tenantId, 'users');
        // Fetch both roles in a single query
        const q = query(staffRef, where('role', 'in', ['Case Manager', 'ECE Recorder']));
        const snap = await getDocs(q);
        
        const managers = [];
        const recorders = [];

        snap.docs.forEach(doc => {
          const data = doc.data();
          const staffMember = {
            id: doc.id,
            name: data.name || 'Unnamed Staff',
            email: data.email
          };

          if (data.role === 'Case Manager') {
            managers.push(staffMember);
          } else if (data.role === 'ECE Recorder') {
            recorders.push(staffMember);
          }
        });
        
        setAvailableManagers(managers);
        setAvailableEceRecorders(recorders);
      } catch (err) {
        console.error("Error fetching staff:", err);
        setError("Could not load staff list.");
      }
    };

    fetchStaff();
  }, [tenantId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Ensure both staff members are assigned
    if (!studentName || !selectedCaseManager || !selectedEceRecorder || !schoolId) {
      setError("All fields are required.");
      setIsSubmitting(false);
      return;
    }

    try {
      const studentsRef = collection(db, 'districts', tenantId, 'students');
      
      // Create the clean slate student record
      await addDoc(studentsRef, {
        studentName: studentName,
        assignedCaseManager: selectedCaseManager,
        assignedEceRecorder: selectedEceRecorder, // <-- NEW ASSIGNMENT
        schoolId: schoolId,
        annualDate: null,
        reevalDate: null,
        initialDate: null,
        trackingInitial: false,
        createdAt: new Date()
      });

      setStudentName('');
      setSelectedCaseManager('');
      setSelectedEceRecorder('');
      setSchoolId('');
      onSuccess(); // Tell the dashboard to refresh and close the form
    } catch (err) {
      console.error("Error adding student:", err);
      setError("Failed to save student to the vault.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
      <h3 style={{ marginTop: 0, color: '#003366', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
        Add New Student
      </h3>
      
      {error && <p style={{ color: 'red', fontSize: '14px', fontWeight: 'bold' }}>{error}</p>}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        
        <div>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Student Name</label>
          <input 
            type="text" 
            value={studentName} 
            onChange={(e) => setStudentName(e.target.value)} 
            placeholder="e.g., Jane Doe"
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Assigned Building</label>
          <select 
            value={schoolId} 
            onChange={(e) => setSchoolId(e.target.value)}
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
          >
            <option value="">-- Select Building --</option>
            {schools.length > 0 ? (
              schools.map((school, index) => (
                <option key={index} value={school}>{school}</option>
              ))
            ) : (
              <option value="" disabled>No buildings configured for this district</option>
            )}
          </select>
        </div>

        {/* CASE MANAGER DROPDOWN */}
        <div>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Case Manager</label>
          <select 
            value={selectedCaseManager} 
            onChange={(e) => setSelectedCaseManager(e.target.value)}
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
          >
            <option value="">-- Select Case Manager --</option>
            {availableManagers.map(manager => (
              <option key={manager.id} value={manager.id}>
                {manager.name} ({manager.email})
              </option>
            ))}
          </select>
        </div>

        {/* NEW: ECE RECORDER DROPDOWN */}
        <div>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Assigned ECE Recorder (Auditor)</label>
          <select 
            value={selectedEceRecorder} 
            onChange={(e) => setSelectedEceRecorder(e.target.value)}
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
          >
            <option value="">-- Select ECE Recorder --</option>
            {availableEceRecorders.map(recorder => (
              <option key={recorder.id} value={recorder.id}>
                {recorder.name} ({recorder.email})
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button 
            type="submit" 
            disabled={isSubmitting}
            style={{ padding: '10px 20px', backgroundColor: '#ba0c2f', color: 'white', border: 'none', borderRadius: '4px', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}
          >
            {isSubmitting ? 'Saving...' : 'Add Student'}
          </button>
          
          <button 
            type="button" 
            onClick={onCancel}
            style={{ padding: '10px 20px', backgroundColor: '#ccc', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddStudentForm;